import L from 'leaflet'
import { FlightViewMapLibMap } from '../map'
import { FlightViewMapLibAD } from '.'
import { format } from 'date-fns'
import { getLocale } from '../tracker/utils'
import { ADDelay } from './types'

const host = location.host.endsWith('flightview.com') ? location.host : 'tracker.flightview.com'

export class FlightViewMapLibADLegend {
  ad: FlightViewMapLibAD
  map: L.Map
  timeFormat: string
  dateFormat: string
  locale: string
  displayClock: boolean = true
  legendPosition: string

  constructor (ad: FlightViewMapLibAD, mapModule: FlightViewMapLibMap, timeFormat: string, dateFormat: string, locale: string) {
    this.ad = ad
    this.map = ad.map
    this.timeFormat = timeFormat || 'h:mm a'
    this.dateFormat = dateFormat || 'MMM d'
    this.locale = locale
    this.displayClock = ad.props.displayClock !== undefined ? ad.props.displayClock : true
    this.legendPosition = ad.props.legendPosition || 'bottomleft'

    const legend = L.Control.extend({
      options:
        {
          position: this.legendPosition
        },
      onAdd: () => {
        const container = L.DomUtil.create('div', 'FVLib-ad-legend')
        container.className = 'FVLib-ad-static-legend'
        container.id = 'FVLib-ad-static-legend'
        container.appendChild(this.createStatusExplanation())
        if (this.displayClock) {
          container.appendChild(this.createDateTimeContent())
          this.scheduleUpdateTime()
        }
        container.appendChild(this.createOAGLogo())
        return container
      }
    })
    const instance = new legend()
    this.map.addControl(instance)
  }

  scheduleUpdateTime () {
    setInterval(() => this.updateCurrentTime(), 60 * 1000)
  }

  createDateTimeContent () {
    const div = L.DomUtil.create('div', 'FVLib-ad-static-legend-datetime FVLib-ad-static-legend-content')
    const dateTxt = L.DomUtil.create('span', 'FVLib-ad-legend-date FVLib-ad-legend-datetime-label')
    dateTxt.id = 'FVLib-ad-legend-date'
    div.appendChild(dateTxt)

    const timeDiv = L.DomUtil.create('div', 'FVLib-ad-legend-time')
    const timeTxt = L.DomUtil.create('span', 'FVLib-ad-legend-datetime-label')
    timeTxt.id = 'FVLib-ad-legend-time'
    timeDiv.appendChild(timeTxt)

    const meridiemTxt = L.DomUtil.create('span', 'FVLib-ad-legend-meridiem-label')
    meridiemTxt.id = 'FVLib-ad-legend-meridiem'
    timeDiv.appendChild(meridiemTxt)

    div.appendChild(timeDiv)
    this.updateCurrentTime(dateTxt, timeTxt, meridiemTxt)
    return div
  }

  createOAGLogo () {
    const div = L.DomUtil.create('div', 'FVLib-ad-static-legend-oag-logo FVLib-ad-static-legend-content')
    const img = L.DomUtil.create('img', 'FVLib-oagLogo')
    img.src = `https://${host}/images/Logos/OAG/OAG_436x274.png`
    img.style.display = 'block'
    div.appendChild(img)
    return div
  }

  createStatusExplanation () {
    const div = L.DomUtil.create('div', 'FVLib-ad-legend-expl')
    const bigDiamondDiv = L.DomUtil.create('div', 'FVLib-ad-legend-expl-big-diamond', div)
    const arrivalLabel = L.DomUtil.create('span', 'FVLib-ad-legend-expl-arr-label', bigDiamondDiv)
    arrivalLabel.innerHTML = 'Arrival<br/>Delay<br/>Status'
    L.DomUtil.create('div', 'FVLib-ad-legend-expl-arr-label-line', bigDiamondDiv)
    bigDiamondDiv.appendChild(this.createDiamond(ADDelay.OnTime, ADDelay.MajorDelay, '3rem'))
    L.DomUtil.create('div', 'FVLib-ad-legend-expl-dep-label-line', bigDiamondDiv)
    const departureLabel = L.DomUtil.create('span', 'FVLib-ad-legend-expl-dep-label', bigDiamondDiv)
    departureLabel.innerHTML = 'Departure<br/>Delay<br/>Status'

    const colorExpl = L.DomUtil.create('div', 'FVLib-ad-legend-expl-color-wrapper', div)

    colorExpl.appendChild(this.createColorExpl(ADDelay.OnTime, 'On Time'))
    colorExpl.appendChild(this.createColorExpl(ADDelay.Delay, 'Minor Delay'))
    colorExpl.appendChild(this.createColorExpl(ADDelay.MajorDelay, 'Major Delay'))
    return div
  }

  createColorExpl (delay: ADDelay, label: string) {
    const div = L.DomUtil.create('div', 'FVLib-ad-legend-expl-color')
    div.appendChild(this.createDiamond(delay, delay, '1rem'))

    const labelSpan = L.DomUtil.create('span', 'FVLib-ad-legend-expl-color-label')
    labelSpan.innerText = label
    div.appendChild(labelSpan)
    return div
  }

  createDiamond (arrivals: ADDelay, departures: ADDelay, size: string) {
    const div = L.DomUtil.create('div', `FVLib-ad-dot FVLib-ad-dot-${arrivals === ADDelay.OnTime ? 'ontime' : arrivals === ADDelay.Delay ? 'minor' : 'major'}-${departures === ADDelay.OnTime ? 'ontime' : departures === ADDelay.Delay ? 'minor' : 'major'}`)
    div.setAttribute('style', `width: ${size}; height: ${size}`)
    return div
  }

  updateCurrentTime (dateTxtEle?: HTMLElement, timeTxtEle?: HTMLElement, meridiemTxtEle?: HTMLElement) {
    const now = new Date()
    const dateTxt = format(now, this.dateFormat, { locale: getLocale(this.locale) })
    const timeTxt = format(now, this.timeFormat, { locale: getLocale(this.locale) })
    dateTxtEle = dateTxtEle || document.getElementById('FVLib-ad-legend-date')
    dateTxtEle.innerText = dateTxt
    timeTxtEle = timeTxtEle || document.getElementById('FVLib-ad-legend-time')
    timeTxtEle.innerText = timeTxt.split(' ')[0]
    meridiemTxtEle = meridiemTxtEle || document.getElementById('FVLib-ad-legend-meridiem')
    meridiemTxtEle.innerText = timeTxt.split(' ')[1] || ''
  }
}
